
import { createContext, useContext, useReducer, useEffect, useRef } from 'react'
import { authAPI } from '../services/authAPI'
import { toast } from 'react-hot-toast'
import { onAuthStateChanged, getRedirectResult, getAuth, linkWithCredential, fetchSignInMethodsForEmail, signInWithCredential } from 'firebase/auth'
import { auth } from '../lib/firebaseConfig'

export const AuthContext = createContext()

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: true,
  error: null
}

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null
      }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null
      }
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        loading: false
      }
    default:
      return state
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const refreshTimerRef = useRef(null)

  // Function to refresh token before expiration
  const scheduleTokenRefresh = () => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }

    const token = localStorage.getItem('token')
    if (!token) return

    try {
      // Decode token to get expiration time
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expirationTime = payload.exp * 1000 // Convert to milliseconds
      const now = Date.now()
      const timeUntilExpiration = expirationTime - now
      
      // If token is already expired but not too old (within 24 hours), refresh immediately
      // Otherwise, refresh 5 minutes before expiration
      let refreshTime
      if (timeUntilExpiration < 0) {
        // Token is expired, but try to refresh if it's not too old (within 24 hours)
        const timeSinceExpiration = Math.abs(timeUntilExpiration)
        if (timeSinceExpiration < 24 * 60 * 60 * 1000) {
          // Token expired but within 24 hours, refresh immediately
          refreshTime = 0
        } else {
          // Token is too old, don't schedule refresh
          console.warn('Token expired more than 24 hours ago, not scheduling refresh')
          return
        }
      } else {
        // Token is still valid, refresh 5 minutes before expiration (or at least 1 minute)
        refreshTime = Math.max(timeUntilExpiration - 5 * 60 * 1000, 60000)
      }
      
      if (refreshTime >= 0) {
        refreshTimerRef.current = setTimeout(async () => {
          try {
            console.log('Attempting automatic token refresh...')
            const response = await authAPI.refreshToken()
            if (response.data.success && response.data.data.token) {
              const newToken = response.data.data.token
              localStorage.setItem('token', newToken)
              // Token is stored in localStorage and will be used automatically by API interceptor
              // No need to update state as token is not stored in state
              console.log('Token refreshed successfully')
              // Schedule next refresh
              scheduleTokenRefresh()
            } else {
              throw new Error('Token refresh failed: invalid response')
            }
          } catch (error) {
            console.error('Automatic token refresh failed:', error)
            // If refresh fails, try again in 1 minute (token might still be refreshable)
            // Only retry if we have a token (might have been cleared)
            if (localStorage.getItem('token')) {
              refreshTimerRef.current = setTimeout(scheduleTokenRefresh, 60000)
            }
          }
        }, refreshTime)
        if (refreshTime === 0) {
          console.log('Token expired, refreshing immediately')
        } else {
          console.log(`Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`)
        }
      }
    } catch (error) {
      console.error('Error scheduling token refresh:', error)
      // If we can't decode the token, try to refresh it anyway after a short delay
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const response = await authAPI.refreshToken()
          if (response.data.success && response.data.data.token) {
            localStorage.setItem('token', response.data.data.token)
            scheduleTokenRefresh()
          }
        } catch (refreshError) {
          console.error('Failed to refresh invalid token:', refreshError)
        }
      }, 10000) // Try after 10 seconds
    }
  }

  // Check if user is logged in on app start and set up Firebase auth listener
  useEffect(() => {
    let unsubscribe = null

    const handleRedirectResult = async () => {
      try {
        const authInstance = getAuth()
        
        // Check if popup login was already completed - if so, skip redirect processing
        const popupCompleted = localStorage.getItem('sso_popup_completed')
        if (popupCompleted) {
          const completedTime = parseInt(popupCompleted, 10)
          const elapsed = Date.now() - completedTime
          // If popup completed recently (within last 30 seconds), skip redirect processing
          if (elapsed < 30000) {
            console.log('[SSO][Redirect] Skipping redirect processing - popup login already completed', {
              completedTime: new Date(completedTime).toISOString(),
              elapsed: `${Math.round(elapsed/1000)}s ago`
            })
            // Clean up the flag after a delay
            setTimeout(() => {
              localStorage.removeItem('sso_popup_completed')
            }, 5000)
            return
          } else {
            // Clean up old flag
            localStorage.removeItem('sso_popup_completed')
          }
        }
        
        // IMPORTANT: Set tenant ID BEFORE calling getRedirectResult
        // The tenant ID must be set on the auth instance for multi-tenant redirects to work
        // Check both sessionStorage (primary) and localStorage (backup for redirects)
        let storedTenantId = sessionStorage.getItem('sso_tenant_id')
        let storedEmail = sessionStorage.getItem('sso_email')
        let storedConfig = sessionStorage.getItem('sso_config')
        let storedTimestamp = sessionStorage.getItem('sso_timestamp')
        
        // If sessionStorage is empty but localStorage has redirect markers, restore them
        // This handles cases where sessionStorage was cleared but redirect is still in progress
        if (!storedTenantId) {
          const backupTenantId = localStorage.getItem('sso_redirect_tenant_id')
          const backupEmail = localStorage.getItem('sso_redirect_email')
          const backupTimestamp = localStorage.getItem('sso_redirect_timestamp')
          
          if (backupTenantId && backupEmail) {
            // Check if redirect is recent (within last 5 minutes)
            const timestamp = parseInt(backupTimestamp || '0', 10)
            const elapsed = Date.now() - timestamp
            const isRecent = elapsed < 5 * 60 * 1000 // 5 minutes
            
            if (isRecent) {
              console.log('[SSO][Redirect] Restoring SSO markers from localStorage backup', {
                tenantId: backupTenantId,
                email: backupEmail,
                elapsed: `${Math.round(elapsed/1000)}s ago`
              })
              
              // Restore to sessionStorage
              storedTenantId = backupTenantId
              storedEmail = backupEmail
              storedTimestamp = backupTimestamp
              sessionStorage.setItem('sso_tenant_id', backupTenantId)
              sessionStorage.setItem('sso_email', backupEmail)
              sessionStorage.setItem('sso_timestamp', backupTimestamp)
              
              // Try to restore config if available
              const backupConfig = localStorage.getItem('sso_redirect_config')
              if (backupConfig) {
                storedConfig = backupConfig
                sessionStorage.setItem('sso_config', backupConfig)
              }
            } else {
              // Clean up old backup markers
              localStorage.removeItem('sso_redirect_tenant_id')
              localStorage.removeItem('sso_redirect_email')
              localStorage.removeItem('sso_redirect_timestamp')
              localStorage.removeItem('sso_redirect_config')
            }
          }
        }
        
        console.log('[SSO][Redirect] Starting redirect result check', {
          hasStoredTenantId: !!storedTenantId,
          storedTenantId,
          hasStoredEmail: !!storedEmail,
          storedEmail,
          hasStoredConfig: !!storedConfig,
          currentAuthTenantId: authInstance.tenantId,
          currentUser: !!authInstance.currentUser,
          referrer: document.referrer,
          url: window.location.href,
          urlParams: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
          hash: window.location.hash
        })
        
        if (storedTenantId) {
          console.log('[SSO][Redirect] Setting tenant ID on auth instance:', storedTenantId)
          authInstance.tenantId = storedTenantId
        } else {
          console.warn('[SSO][Redirect] ⚠️ No tenant ID in sessionStorage or localStorage - redirect may fail!')
        }
        
        // Set up a temporary auth state listener to catch auto-sign-in
        // Firebase might sign the user in asynchronously after redirect
        let authStateResolved = false
        const authStatePromise = new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(authInstance, (user) => {
            console.log('[SSO][Redirect] Auth state changed:', {
              hasUser: !!user,
              userEmail: user?.email,
              tenantId: authInstance.tenantId
            })
            if (user && !authStateResolved) {
              authStateResolved = true
              unsubscribe()
              resolve(user)
            } else if (!user && authStateResolved) {
              // User signed out after being signed in - this shouldn't happen
              unsubscribe()
              resolve(null)
            }
          })
          
          // Timeout after 3 seconds
          setTimeout(() => {
            if (!authStateResolved) {
              authStateResolved = true
              unsubscribe()
              resolve(null)
            }
          }, 3000)
        })
        
        // CRITICAL: For multi-tenant Firebase, the redirect result is stored per tenant
        // We MUST ensure the tenant ID is set BEFORE calling getRedirectResult
        // Also, Firebase might need the auth instance to be fully initialized
        console.log('[SSO][Redirect] Auth instance state before getRedirectResult:', {
          tenantId: authInstance.tenantId,
          currentUser: !!authInstance.currentUser,
          appName: authInstance.app?.name,
          config: {
            apiKey: authInstance.app?.options?.apiKey?.substring(0, 10) + '...',
            authDomain: authInstance.app?.options?.authDomain,
            projectId: authInstance.app?.options?.projectId
          }
        })
        
        // Small delay to ensure Firebase has processed the redirect
        // Sometimes getRedirectResult needs a moment after page load
        await new Promise(resolve => setTimeout(resolve, 200))
        
        let result = null
        try {
          console.log('[SSO][Redirect] Calling getRedirectResult with tenant:', authInstance.tenantId)
          
          // Double-check tenant ID is still set (it might have been cleared)
          if (storedTenantId && authInstance.tenantId !== storedTenantId) {
            console.warn('[SSO][Redirect] ⚠️ Tenant ID was cleared! Resetting to:', storedTenantId)
            authInstance.tenantId = storedTenantId
          }
          
          result = await getRedirectResult(authInstance)
          console.log('[SSO][Redirect] getRedirectResult returned:', {
            hasResult: !!result,
            hasUser: !!result?.user,
            userEmail: result?.user?.email,
            providerId: result?.providerId,
            operationType: result?.operationType,
            tenantId: authInstance.tenantId
          })
          
          // If result is null, check if there's a user already signed in (might have been auto-signed in)
          if (!result && authInstance.currentUser) {
            console.log('[SSO][Redirect] ⚠️ No redirect result but user is signed in - might have been auto-signed in')
            console.log('[SSO][Redirect] Current user:', {
              email: authInstance.currentUser.email,
              uid: authInstance.currentUser.uid,
              tenantId: authInstance.tenantId
            })
          }
        } catch (redirectError) {
          // getRedirectResult can throw errors - log them
          console.error('[SSO][Redirect] ❌ getRedirectResult threw error:', redirectError)
          console.error('[SSO][Redirect] Error code:', redirectError.code)
          console.error('[SSO][Redirect] Error message:', redirectError.message)
          console.error('[SSO][Redirect] Error stack:', redirectError.stack)
          console.error('[SSO][Redirect] Full error object:', redirectError)
          console.error('[SSO][Redirect] Auth instance state during error:', {
            tenantId: authInstance.tenantId,
            currentUser: !!authInstance.currentUser
          })
          // Re-throw to be handled by outer catch block
          throw redirectError
        }
        
        // Wait for auth state listener to catch any async sign-in
        // This handles cases where Firebase signs the user in after getRedirectResult returns null
        if (!result && storedEmail && storedTenantId) {
          console.log('[SSO][Redirect] Waiting for auth state listener to catch async sign-in...')
          const asyncUser = await authStatePromise
          if (asyncUser) {
            console.log('[SSO][Redirect] ✅ Auth state listener detected user sign-in:', asyncUser.email)
            // Process the async sign-in as if it were a redirect result
            result = { user: asyncUser }
          }
        }
        
        if (result) {
          console.log('[SSO][Redirect] Redirect result received', result.user?.email)
          
          // Check if popup login already completed - if so, skip processing
          const popupCompleted = localStorage.getItem('sso_popup_completed')
          if (popupCompleted) {
            const completedTime = parseInt(popupCompleted, 10)
            const elapsed = Date.now() - completedTime
            if (elapsed < 30000) {
              console.log('[SSO][Redirect] Popup login already completed, skipping redirect processing')
              // Clean up markers
              sessionStorage.removeItem('sso_email')
              sessionStorage.removeItem('sso_tenant_id')
              sessionStorage.removeItem('sso_config')
              sessionStorage.removeItem('sso_timestamp')
              localStorage.removeItem('sso_redirect_tenant_id')
              localStorage.removeItem('sso_redirect_email')
              localStorage.removeItem('sso_redirect_timestamp')
              localStorage.removeItem('sso_redirect_config')
              localStorage.removeItem('sso_popup_completed')
              return
            }
          }
          
          // Get stored email and tenant ID from sessionStorage
          const storedEmail = sessionStorage.getItem('sso_email')
          const tenantIdFromStorage = sessionStorage.getItem('sso_tenant_id')
          
          // Ensure tenant ID is still set (in case it was cleared)
          if (tenantIdFromStorage && !authInstance.tenantId) {
            authInstance.tenantId = tenantIdFromStorage
          }
          
          // Clean up sessionStorage AND localStorage AFTER getting the result
          sessionStorage.removeItem('sso_email')
          sessionStorage.removeItem('sso_tenant_id')
          sessionStorage.removeItem('sso_config')
          sessionStorage.removeItem('sso_timestamp')
          // Also clean up localStorage backup
          localStorage.removeItem('sso_redirect_tenant_id')
          localStorage.removeItem('sso_redirect_email')
          localStorage.removeItem('sso_redirect_timestamp')
          localStorage.removeItem('sso_redirect_config')
          localStorage.removeItem('sso_popup_completed')
          
          // Get ID token from Firebase
          const idToken = await result.user.getIdToken()
          // Normalize email (lowercase, trim)
          const normalizeEmail = (email) => email ? email.trim().toLowerCase() : ''
          const emailToUse = normalizeEmail(storedEmail || result.user.email)
          
          if (!emailToUse) {
            console.error('[SSO][Redirect] No email available for login')
            toast.error('Unable to complete login: email not found')
            await authInstance.signOut()
            return
          }
          
          console.log('[SSO][Redirect] Completing login with backend', { 
            email: emailToUse, 
            tenantId: authInstance.tenantId,
            firebaseUid: result.user.uid 
          })
          
          // Complete login with backend
          try {
            const response = await authAPI.login({ email: emailToUse, idToken })
            console.log('[SSO][Redirect] Backend login response received', response.data)
            
            // Check if MFA is required
            if (response.data.requires_mfa && response.data.data?.session_token) {
              console.log('[SSO][Redirect] MFA verification required')
              // Store MFA session token - the Login component will handle this
              sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
              sessionStorage.setItem('mfa_email', emailToUse)
              // Redirect to login page to show MFA component
              window.location.href = '/login?mfa=required'
              return
            }
            
            if (!response.data.success || !response.data.data) {
              console.error('[SSO][Redirect] Backend login returned unsuccessful response:', response.data)
              throw new Error(response.data.message || 'Login failed')
            }
            
            const { user, token } = response.data.data
            
            if (!token) {
              console.error('[SSO][Redirect] No token in backend response')
              throw new Error('No authentication token received')
            }
            
            localStorage.setItem('token', token)
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user, token }
            })
            
            // Check if we need to link password after Azure login (reverse linking)
            const linkPasswordFlag = sessionStorage.getItem('link_password_after_azure')
            const linkPasswordEmail = sessionStorage.getItem('link_password_email')
            const linkPasswordTenantId = sessionStorage.getItem('link_password_tenant_id')
            
            if (linkPasswordFlag === 'true' && linkPasswordEmail && normalizeEmail(linkPasswordEmail) === emailToUse) {
              console.log('[SSO][Redirect] Password linking flag detected - will link password to Azure account')
              // Note: We can't link password without the password, so we'll inform user
              // Password will be linked when user logs in with password next time
              toast('Password will be linked automatically when you login with password next time.', { duration: 6000 })
              sessionStorage.removeItem('link_password_after_azure')
              sessionStorage.removeItem('link_password_email')
              sessionStorage.removeItem('link_password_tenant_id')
            }
            
            // Schedule automatic token refresh
            scheduleTokenRefresh()
            
            console.log('[SSO][Redirect] Login successful, redirecting to dashboard')
            toast.success('SSO login successful!')
            
            // Redirect to dashboard
            window.location.href = '/dashboard'
          } catch (loginError) {
            console.error('[SSO][Redirect] Backend login failed:', loginError)
            console.error('[SSO][Redirect] Error details:', {
              message: loginError.message,
              response: loginError.response?.data,
              status: loginError.response?.status,
              stack: loginError.stack
            })
            toast.error(loginError.response?.data?.message || loginError.message || 'SSO login failed')
            // Sign out from Firebase
            await authInstance.signOut()
            // Clear sessionStorage on error
            sessionStorage.removeItem('sso_email')
            sessionStorage.removeItem('sso_tenant_id')
            sessionStorage.removeItem('sso_config')
          }
        } else {
          // No redirect result - check if we just came from a redirect
          // Sometimes getRedirectResult returns null even after redirect due to timing/race conditions
          const isFromFirebaseRedirect = document.referrer.includes('firebaseapp.com') || 
                                         document.referrer.includes('__/auth/handler') ||
                                         document.referrer.includes('microsoftonline.com')
          
          // Also check current URL for redirect parameters (code, state, etc.)
          const urlParams = new URLSearchParams(window.location.search)
          const hashParams = new URLSearchParams(window.location.hash.substring(1))
          const hasRedirectParams = urlParams.has('code') || urlParams.has('state') || 
                                    hashParams.has('code') || hashParams.has('state') ||
                                    window.location.hash.includes('code') || 
                                    window.location.hash.includes('state')
          
          const storedEmail = sessionStorage.getItem('sso_email')
          const storedTenantId = sessionStorage.getItem('sso_tenant_id')
          const storedTimestamp = sessionStorage.getItem('sso_timestamp')
          const currentUser = authInstance.currentUser
          
          // Check if SSO redirect was recent (within last 5 minutes)
          const isRecentSSOAttempt = storedTimestamp && (Date.now() - parseInt(storedTimestamp)) < 5 * 60 * 1000
          
          console.log('[SSO][Redirect] No redirect result - checking for redirect indicators', {
            isFromFirebaseRedirect,
            hasRedirectParams,
            storedEmail: !!storedEmail,
            storedEmailValue: storedEmail,
            storedTenantId: !!storedTenantId,
            storedTenantIdValue: storedTenantId,
            storedTimestamp,
            isRecentSSOAttempt,
            currentUser: !!currentUser,
            authTenantId: authInstance.tenantId,
            referrer: document.referrer,
            url: window.location.href,
            urlParams: Object.fromEntries(urlParams.entries()),
            hashParams: Object.fromEntries(hashParams.entries()),
            hash: window.location.hash
          })
          
          // If we have SSO markers and it's a recent attempt, try to retry getRedirectResult
          // Sometimes Firebase needs a moment to process the redirect, especially with multi-tenant
          if ((isFromFirebaseRedirect || hasRedirectParams || isRecentSSOAttempt) && storedEmail && storedTenantId && !currentUser) {
            console.log('[SSO][Redirect] ⚠️ Redirect indicators found but no result - retrying getRedirectResult after delay')
            console.log('[SSO][Redirect] Retry conditions:', {
              isFromFirebaseRedirect,
              hasRedirectParams,
              isRecentSSOAttempt,
              timeSinceRedirect: storedTimestamp ? `${Math.round((Date.now() - parseInt(storedTimestamp)) / 1000)}s ago` : 'unknown',
              storedTenantId,
              currentAuthTenantId: authInstance.tenantId
            })
            
            // CRITICAL: Check if user was auto-signed in by Firebase (sometimes happens silently)
            if (authInstance.currentUser) {
              console.log('[SSO][Redirect] ✅ User is signed in! Processing auto-sign-in:', authInstance.currentUser.email)
              const normalizeEmail = (email) => email ? email.trim().toLowerCase() : ''
              try {
                const idToken = await authInstance.currentUser.getIdToken()
                const emailToUse = normalizeEmail(storedEmail || authInstance.currentUser.email)
                
                console.log('[SSO][Redirect] Completing login with backend (auto-sign-in)', { 
                  email: emailToUse, 
                  tenantId: authInstance.tenantId,
                  firebaseUid: authInstance.currentUser.uid 
                })
                
                const response = await authAPI.login({ email: emailToUse, idToken })
                
                if (response.data.requires_mfa && response.data.data?.session_token) {
                  sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                  sessionStorage.setItem('mfa_email', emailToUse)
                  window.location.href = '/login?mfa=required'
                  return
                }
                
                const { user, token } = response.data.data
                localStorage.setItem('token', token)
                dispatch({
                  type: 'LOGIN_SUCCESS',
                  payload: { user, token }
                })
                
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                sessionStorage.removeItem('sso_timestamp')
                
                scheduleTokenRefresh()
                console.log('[SSO][Redirect] ✅ Login successful (auto-sign-in)')
                toast.success('SSO login successful!')
                window.location.href = '/dashboard'
                return
              } catch (loginError) {
                console.error('[SSO][Redirect] Backend login failed (auto-sign-in):', loginError)
                toast.error(loginError.response?.data?.message || 'Login failed')
                await authInstance.signOut()
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                return
              }
            }
            
            // Retry with progressively longer delays (up to 2 seconds total)
            for (let attempt = 1; attempt <= 3; attempt++) {
              const delay = attempt * 500 // 500ms, 1000ms, 1500ms
              console.log(`[SSO][Redirect] Retry attempt ${attempt}/${3} after ${delay}ms delay`)
              await new Promise(resolve => setTimeout(resolve, delay))
              
              // CRITICAL: Ensure tenant ID is set before each retry
              if (storedTenantId) {
                if (authInstance.tenantId !== storedTenantId) {
                  console.log(`[SSO][Redirect] Retry ${attempt}: Setting tenant ID:`, storedTenantId)
                  authInstance.tenantId = storedTenantId
                } else {
                  console.log(`[SSO][Redirect] Retry ${attempt}: Tenant ID already set:`, storedTenantId)
                }
              }
              
              try {
                const retryResult = await getRedirectResult(authInstance)
                if (retryResult) {
                  console.log(`[SSO][Redirect] ✅ Retry ${attempt} successful! Got redirect result:`, retryResult.user?.email)
                  const normalizeEmail = (email) => email ? email.trim().toLowerCase() : ''
                  const idToken = await retryResult.user.getIdToken()
                  const emailToUse = normalizeEmail(storedEmail || retryResult.user.email)
                  
                  console.log('[SSO][Redirect] Completing login with backend (retry)', { 
                    email: emailToUse, 
                    tenantId: authInstance.tenantId,
                    firebaseUid: retryResult.user.uid 
                  })
                  
                  try {
                    const response = await authAPI.login({ email: emailToUse, idToken })
                    
                    if (response.data.requires_mfa && response.data.data?.session_token) {
                      sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                      sessionStorage.setItem('mfa_email', emailToUse)
                      window.location.href = '/login?mfa=required'
                      return
                    }
                    
                    const { user, token } = response.data.data
                    localStorage.setItem('token', token)
                    dispatch({
                      type: 'LOGIN_SUCCESS',
                      payload: { user, token }
                    })
                    
                    sessionStorage.removeItem('sso_email')
                    sessionStorage.removeItem('sso_tenant_id')
                    sessionStorage.removeItem('sso_config')
                    sessionStorage.removeItem('sso_timestamp')
                    
                    scheduleTokenRefresh()
                    console.log('[SSO][Redirect] ✅ Login successful (retry)')
                    toast.success('SSO login successful!')
                    window.location.href = '/dashboard'
                    return
                  } catch (loginError) {
                    console.error('[SSO][Redirect] Backend login failed (retry):', loginError)
                    toast.error(loginError.response?.data?.message || 'Login failed')
                    await authInstance.signOut()
                    sessionStorage.removeItem('sso_email')
                    sessionStorage.removeItem('sso_tenant_id')
                    sessionStorage.removeItem('sso_config')
                    return
                  }
                } else {
                  console.log(`[SSO][Redirect] Retry ${attempt} still returned null`)
                  // Check again if user was signed in during retry
                  if (authInstance.currentUser) {
                    console.log(`[SSO][Redirect] Retry ${attempt}: User signed in during retry, processing...`)
                    // Process the signed-in user (same as above)
                    const normalizeEmail = (email) => email ? email.trim().toLowerCase() : ''
                    try {
                      const idToken = await authInstance.currentUser.getIdToken()
                      const emailToUse = normalizeEmail(storedEmail || authInstance.currentUser.email)
                      const response = await authAPI.login({ email: emailToUse, idToken })
                      
                      if (response.data.requires_mfa && response.data.data?.session_token) {
                        sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                        sessionStorage.setItem('mfa_email', emailToUse)
                        window.location.href = '/login?mfa=required'
                        return
                      }
                      
                      const { user, token } = response.data.data
                      localStorage.setItem('token', token)
                      dispatch({
                        type: 'LOGIN_SUCCESS',
                        payload: { user, token }
                      })
                      
                      sessionStorage.removeItem('sso_email')
                      sessionStorage.removeItem('sso_tenant_id')
                      sessionStorage.removeItem('sso_config')
                      sessionStorage.removeItem('sso_timestamp')
                      
                      scheduleTokenRefresh()
                      console.log('[SSO][Redirect] ✅ Login successful (retry - user signed in)')
                      toast.success('SSO login successful!')
                      window.location.href = '/dashboard'
                      return
                    } catch (loginError) {
                      console.error('[SSO][Redirect] Backend login failed (retry - user signed in):', loginError)
                      toast.error(loginError.response?.data?.message || 'Login failed')
                      await authInstance.signOut()
                      sessionStorage.removeItem('sso_email')
                      sessionStorage.removeItem('sso_tenant_id')
                      sessionStorage.removeItem('sso_config')
                      return
                    }
                  }
                }
              } catch (retryError) {
                console.error(`[SSO][Redirect] Retry ${attempt} threw error:`, retryError)
                if (attempt === 3) {
                  console.error('[SSO][Redirect] ❌ All retry attempts failed. Possible causes:')
                  console.error('1. Tenant ID mismatch - check if tenant ID matches the one used during signInWithRedirect')
                  console.error('2. Redirect result was already consumed by another component')
                  console.error('3. Firebase redirect handler failed silently')
                  console.error('4. Browser security settings blocking redirect result')
                  console.error('5. Firebase multi-tenant redirect result storage issue')
                  toast.error('SSO redirect failed. The redirect completed but Firebase could not retrieve the result. Please try logging in again.')
                }
              }
            }
          }
          
          // Check if redirect occurred but failed (has markers but no user)
          if ((isFromFirebaseRedirect || hasRedirectParams) && storedEmail && !currentUser) {
            console.warn('[SSO][Redirect] Redirect occurred but Firebase user is signed out - redirect may have failed')
            console.warn('[SSO][Redirect] This could indicate an account-exists error or other redirect failure')
            
            // Check URL for error parameters
            const urlParams = new URLSearchParams(window.location.search)
            const errorParam = urlParams.get('error')
            const errorDescription = urlParams.get('error_description')
            
            if (errorParam || errorDescription) {
              console.error('[SSO][Redirect] Error in URL parameters:', { errorParam, errorDescription })
              toast.error('SSO login failed: ' + (errorDescription || errorParam || 'Unknown error'))
              // Clean up sessionStorage
              sessionStorage.removeItem('sso_email')
              sessionStorage.removeItem('sso_tenant_id')
              sessionStorage.removeItem('sso_config')
              return
            }
            
            // Check if this might be an account-exists error
            // When account-exists happens during redirect, Firebase might sign out the user
            // but not throw an error. Try to detect this by checking sign-in methods
            const storedSSOConfig = sessionStorage.getItem('sso_config')
            let providerName = 'unknown'
            let isLatestProvider = false
            
            try {
              if (storedSSOConfig) {
                const config = JSON.parse(storedSSOConfig)
                providerName = config.provider || 'unknown'
                isLatestProvider = config.isLatestProvider === true
              }
            } catch (e) {
              console.warn('[SSO][Redirect] Could not parse stored SSO config:', e)
            }
            
            const isAzureSSO = (providerName === 'azure' || providerName === 'microsoft')
            
            // If Azure SSO is configured and we have an email, try to check for account-exists
            if (isAzureSSO && storedEmail) {
              console.log('[SSO][Redirect] Checking for account-exists scenario with Azure SSO')
              try {
                const { fetchSignInMethodsForEmail } = await import('firebase/auth')
                const signInMethods = await fetchSignInMethodsForEmail(authInstance, storedEmail)
                console.log('[SSO][Redirect] Existing sign-in methods found:', signInMethods)
                
                // If account exists with password but Azure SSO is configured, this is likely account-exists
                if (signInMethods.includes('password') && signInMethods.length > 0) {
                  console.log('[SSO][Redirect] Account exists with different provider - Azure SSO should link automatically')
                  // Show helpful message
                  toast.error('Your account exists with a different sign-in method. Azure SSO will be linked automatically. Please try signing in with Azure SSO again.', { duration: 8000 })
                  // Don't clear sessionStorage - let user try again
                  return
                }
              } catch (checkError) {
                console.error('[SSO][Redirect] Error checking sign-in methods:', checkError)
              }
            }
            
            // If we have SSO markers but no user, the redirect likely failed silently
            // This often happens with account-exists errors that don't throw exceptions
            console.warn('[SSO][Redirect] Redirect failed silently - clearing SSO markers')
            toast.error('SSO login failed. Please try again or use your existing login method.')
            sessionStorage.removeItem('sso_email')
            sessionStorage.removeItem('sso_tenant_id')
            sessionStorage.removeItem('sso_config')
            return
          }
          
          // If we came from redirect and have sessionStorage markers, but no redirect result,
          // check if Firebase user is signed in (redirect might have succeeded but result wasn't captured)
          if (isFromFirebaseRedirect && storedEmail && currentUser && !localStorage.getItem('token')) {
            console.log('[SSO][Redirect] No redirect result but Firebase user signed in - completing login')
            
            try {
              // Ensure tenant ID is set
              if (storedTenantId && !authInstance.tenantId) {
                authInstance.tenantId = storedTenantId
              }
              
              const idToken = await currentUser.getIdToken()
              const normalizeEmail = (email) => email ? email.trim().toLowerCase() : ''
              const emailToUse = normalizeEmail(storedEmail || currentUser.email)
              
              if (!emailToUse) {
                console.error('[SSO][Redirect] No email available')
                await authInstance.signOut()
                return
              }
              
              console.log('[SSO][Redirect] Completing login with backend (fallback)', { email: emailToUse })
              
              const response = await authAPI.login({ email: emailToUse, idToken })
              
              if (response.data.requires_mfa && response.data.data?.session_token) {
                sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                sessionStorage.setItem('mfa_email', emailToUse)
                window.location.href = '/login?mfa=required'
                return
              }
              
              if (!response.data.success || !response.data.data) {
                throw new Error(response.data.message || 'Login failed')
              }
              
              const { user, token } = response.data.data
              
              if (!token) {
                throw new Error('No authentication token received')
              }
              
              localStorage.setItem('token', token)
              dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { user, token }
              })
              
              scheduleTokenRefresh()
              
              // Clean up sessionStorage
              sessionStorage.removeItem('sso_email')
              sessionStorage.removeItem('sso_tenant_id')
              sessionStorage.removeItem('sso_config')
              
              console.log('[SSO][Redirect] Login successful (fallback)')
              toast.success('SSO login successful!')
              window.location.href = '/dashboard'
            } catch (loginError) {
              console.error('[SSO][Redirect] Backend login failed (fallback):', loginError)
              toast.error(loginError.response?.data?.message || 'Login failed')
              await authInstance.signOut()
              sessionStorage.removeItem('sso_email')
              sessionStorage.removeItem('sso_tenant_id')
              sessionStorage.removeItem('sso_config')
            }
          } else {
            // No redirect result and no indicators of redirect - this is normal
            console.log('[SSO][Redirect] No redirect result (normal if no SSO redirect occurred)')
          }
        }
      } catch (error) {
        console.error('[SSO][Redirect] Error handling redirect result:', error)
        console.error('[SSO][Redirect] Error code:', error.code)
        console.error('[SSO][Redirect] Error message:', error.message)
        
        // Handle account-exists-with-different-credential / provider conflict
        // Also handle variations of the error message
        if (error.code === 'auth/account-exists-with-different-credential' ||
            error.code === 'auth/credential-already-in-use' ||
            error.message?.includes('existing provider') ||
            error.message?.includes('already associated')) {
          
          // Check if the provider being used should be prioritized
          // Get stored SSO config from session to check isLatestProvider and provider name
          const storedSSOConfig = sessionStorage.getItem('sso_config')
          let isLatestProvider = false
          let providerName = 'unknown'
          
          try {
            if (storedSSOConfig) {
              const config = JSON.parse(storedSSOConfig)
              isLatestProvider = config.isLatestProvider === true
              providerName = config.provider || 'unknown'
            }
          } catch (e) {
            console.warn('[SSO][Redirect] Could not parse stored SSO config:', e)
          }
          
          // Only prioritize if this is the latest provider (by creation date)
          // Azure/Microsoft are NOT automatically prioritized - only if they're the latest
          const shouldPrioritizeProvider = isLatestProvider === true

          if (!shouldPrioritizeProvider) {
            console.log('[SSO][Redirect] Account exists with different credential, but this provider is not the latest - using standard Firebase behavior')
            toast.error('An account with this email already exists with a different sign-in method. Please sign in with your existing method.')
            return
          }
          
          console.log(`[SSO][Redirect] Account exists with different credential - ${providerName} is the latest provider, prioritizing login and linking`)
          console.log('[SSO][Redirect] Error details:', { code: error.code, message: error.message, error })
          
          try {
            // Extract email and credential from error
            // Firebase error structure: error.customData.email and error.credential
            const email = error.customData?.email || error.email
            const credential = error.credential
            
            console.log('[SSO][Redirect] Extracted from error:', { email: !!email, hasCredential: !!credential })
            
            // Get email - from error or stored session
            const emailToUse = email || sessionStorage.getItem('sso_email')
            
            if (!emailToUse) {
              console.error('[SSO][Redirect] Missing email in error and session')
              toast.error('Unable to link Azure account. Please try signing in with your existing method first.')
              return
            }
            
            if (!credential) {
              console.error('[SSO][Redirect] Missing credential in error - cannot link providers')
              toast.error('Unable to link Azure provider (latest provider). The account may need to be linked manually.')
              return
            }
            
            // Fetch sign-in methods for this email
            const signInMethods = await fetchSignInMethodsForEmail(authInstance, emailToUse)
            console.log('[SSO][Redirect] Existing sign-in methods:', signInMethods)
            console.log('[SSO][Redirect] Azure is the latest provider - will link Azure to existing account')
            
            // Azure is the latest provider integrated in ProductHub
            // Prioritize Azure login even if other providers exist
            // First, check if user is already signed in OR if we have a backend token (user was logged in before redirect)
            const hasBackendToken = localStorage.getItem('token')
            const wasLoggedInBeforeRedirect = hasBackendToken || sessionStorage.getItem('was_logged_in_before_sso') === 'true'
            
            if (authInstance.currentUser || wasLoggedInBeforeRedirect) {
              // If user is not signed in Firebase but was logged in before redirect, we need to sign them in first
              if (!authInstance.currentUser && wasLoggedInBeforeRedirect) {
                console.log('[SSO][Redirect] User was logged in before redirect but Firebase signed out - need to sign in Firebase first')
                // We can't sign in Firebase without password, so we'll handle this in Login component
                // Store flag to trigger password sign-in + Azure linking
                sessionStorage.setItem('link_azure_after_password', 'true')
                sessionStorage.setItem('link_azure_email', emailToUse)
                sessionStorage.removeItem('was_logged_in_before_sso')
                toast('Please sign in with your password. Azure SSO will be linked automatically.', { duration: 8000 })
                window.location.href = '/login'
                return
              }
              
              // Verify that the signed-in user's email matches the SSO email
              const currentUserEmail = authInstance.currentUser?.email ? authInstance.currentUser.email.trim().toLowerCase() : null
              const ssoEmailNormalized = emailToUse.trim().toLowerCase()
              
              if (currentUserEmail && currentUserEmail !== ssoEmailNormalized) {
                console.warn('[SSO][Redirect] Signed-in user email does not match SSO email', {
                  currentUserEmail,
                  ssoEmail: ssoEmailNormalized
                })
                toast.error(`You are signed in with a different account (${currentUserEmail}). Please sign out and try again.`)
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                sessionStorage.removeItem('sso_timestamp')
                sessionStorage.removeItem('was_logged_in_before_sso')
                return
              }
              
              // User is signed in with matching email, link the credential (latest provider takes precedence)
              console.log(`[SSO][Redirect] User already signed in with matching email (${currentUserEmail}), attempting to link ${providerName} provider`)
              
              try {
                // Attempt to link the Azure credential to the existing account
                // This allows the user to use either the existing provider (password) or Azure SSO in the future
                console.log(`[SSO][Redirect] 🔗 Linking ${providerName} credential to existing account (user signed in with password)`)
                console.log(`[SSO][Redirect] Before linking - User email: ${authInstance.currentUser.email}, UID: ${authInstance.currentUser.uid}`)
                
                // Check methods BEFORE linking
                const methodsBeforeLinking = await fetchSignInMethodsForEmail(authInstance, emailToUse)
                console.log(`[SSO][Redirect] Methods BEFORE linking:`, methodsBeforeLinking)
                
                try {
                  await linkWithCredential(authInstance.currentUser, credential)
                  console.log(`[SSO][Redirect] ✅ linkWithCredential succeeded`)
                } catch (linkError) {
                  console.error(`[SSO][Redirect] ❌ linkWithCredential FAILED:`, {
                    code: linkError.code,
                    message: linkError.message,
                    stack: linkError.stack
                  })
                  throw linkError
                }
                
                // ✅ FIX: Verify linking succeeded by checking sign-in methods
                // Wait a bit for Firebase to update
                await new Promise(resolve => setTimeout(resolve, 500))
                
                const methodsAfterLinking = await fetchSignInMethodsForEmail(authInstance, emailToUse)
                const providerId = providerName === 'azure' || providerName === 'microsoft' ? 'oidc.myazure' : 'oidc.generic'
                
                console.log(`[SSO][Redirect] Methods AFTER linking:`, methodsAfterLinking)
                console.log(`[SSO][Redirect] Looking for provider: ${providerId}`)
                
                if (!methodsAfterLinking.includes(providerId) && !methodsAfterLinking.includes('oidc.myazure')) {
                  console.error(`[SSO][Redirect] ❌ LINKING VERIFICATION FAILED!`)
                  console.error(`[SSO][Redirect] Expected: ${providerId} or oidc.myazure`)
                  console.error(`[SSO][Redirect] Found: ${methodsAfterLinking.join(', ')}`)
                  throw new Error(`Linking verification failed - ${providerName} provider not found in sign-in methods. Found: ${methodsAfterLinking.join(', ')}`)
                }
                console.log(`[SSO][Redirect] ✅ Verified: Successfully linked ${providerName} credential. Sign-in methods:`, methodsAfterLinking)
                
                // Get updated ID token
                const idToken = await authInstance.currentUser.getIdToken()
                
                // Complete login with backend
                try {
                  const response = await authAPI.login({ email: emailToUse, idToken })
                  
                  if (response.data.requires_mfa && response.data.data?.session_token) {
                    sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                    sessionStorage.setItem('mfa_email', emailToUse)
                    sessionStorage.removeItem('sso_email')
                    sessionStorage.removeItem('sso_tenant_id')
                    sessionStorage.removeItem('sso_config')
                    sessionStorage.removeItem('sso_timestamp')
                    window.location.href = '/login?mfa=required'
                    return
                  }
                  
                  if (!response.data.success || !response.data.data) {
                    throw new Error(response.data.message || 'Login failed')
                  }
                  
                  const { user, token } = response.data.data
                  
                  if (!token) {
                    throw new Error('No authentication token received')
                  }
                  
                  localStorage.setItem('token', token)
                  dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: { user, token }
                  })
                  
                  scheduleTokenRefresh()
                  
                  // Clean up sessionStorage
                  sessionStorage.removeItem('sso_email')
                  sessionStorage.removeItem('sso_tenant_id')
                  sessionStorage.removeItem('sso_config')
                  sessionStorage.removeItem('sso_timestamp')
                  
                  toast.success(
                    `Azure SSO has been successfully linked to your account! You can now use either password or Azure SSO to login.`,
                    { duration: 6000 }
                  )
                  console.log(`[SSO][Redirect] Account linking complete - user can now login with password or ${providerName}`)
                  window.location.href = '/dashboard'
                  return
                } catch (loginError) {
                  console.error('[SSO][Redirect] Backend login failed after linking:', loginError)
                  toast.error(loginError.response?.data?.message || 'SSO login failed')
                  await authInstance.signOut()
                  sessionStorage.removeItem('sso_email')
                  sessionStorage.removeItem('sso_tenant_id')
                  sessionStorage.removeItem('sso_config')
                  sessionStorage.removeItem('sso_timestamp')
                  return
                }
              } catch (linkError) {
                console.error('[SSO][Redirect] Failed to link credential:', linkError)
                console.error('[SSO][Redirect] Link error details:', {
                  code: linkError.code,
                  message: linkError.message,
                  stack: linkError.stack
                })
                
                // Show specific error message based on error type
                let errorMessage = 'Unable to link Azure account. '
                
                if (linkError.code === 'auth/credential-already-in-use') {
                  errorMessage += 'Azure is already linked to another account.'
                } else if (linkError.code === 'auth/email-already-in-use') {
                  errorMessage += 'This email is already in use with a different account.'
                } else if (linkError.code === 'auth/invalid-credential') {
                  errorMessage += 'The Azure credential is invalid or has expired. Please try again.'
                } else if (linkError.code === 'auth/provider-already-linked') {
                  errorMessage += 'Azure is already linked to this account.'
                } else if (linkError.message?.includes('expired')) {
                  errorMessage += 'The Azure credential has expired. Please try again.'
                } else {
                  errorMessage += `Unable to link Azure provider: ${linkError.message || 'Unknown error'}. Please try again or contact support.`
                }
                
                toast.error(errorMessage, { duration: 8000 })
                
                // Clear SSO markers
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                sessionStorage.removeItem('sso_timestamp')
                
                // Don't sign out - let user continue with their current session
                return
              }
            } else {
              // User is not signed in, but we have a credential from Azure
              // We need to sign in with the existing provider first, then link Azure
              console.log(`[SSO][Redirect] User not signed in - attempting automatic account linking`)
              
              try {
                // Fetch existing sign-in methods to determine how to sign in
                const existingMethods = await fetchSignInMethodsForEmail(authInstance, emailToUse)
                console.log('[SSO][Redirect] Existing sign-in methods found:', existingMethods)
                
                if (!existingMethods || existingMethods.length === 0) {
                  throw new Error('No existing sign-in methods found')
                }
                
                const existingProviderId = existingMethods[0]
                
                // Map Firebase provider IDs to friendly names
                const providerNameMap = {
                  'password': 'Email/Password',
                  'google.com': 'Google',
                  'facebook.com': 'Facebook',
                  'twitter.com': 'Twitter',
                  'github.com': 'GitHub',
                  'oidc.mygoogle': 'Google',
                  'oidc.myazure': 'Microsoft/Azure',
                  'oidc.okta': 'Okta',
                  'oidc.auth0': 'Auth0',
                  'oidc.generic': 'SSO Provider'
                }
                
                const existingProviderName = providerNameMap[existingProviderId] || existingProviderId
                
                // If existing provider is password, we can't automatically sign in (need password)
                // But we can store the credential info and link it after password login
                if (existingProviderId === 'password') {
                  console.log('[SSO][Redirect] Existing provider is password - will link Azure after password login')
                  
                  // Store flag to trigger Azure linking after password login
                  // Keep SSO config and email so we can trigger Azure SSO again after password login
                  sessionStorage.setItem('link_azure_after_password', 'true')
                  sessionStorage.setItem('link_azure_email', emailToUse)
                  // Keep sso_config and sso_tenant_id for the linking attempt
                  
                  toast(
                    `Please sign in with your password. Azure SSO will be linked automatically after login.`,
                    { duration: 8000 }
                  )
                  // Don't clear SSO markers - we need them for linking after password login
                  window.location.href = '/login'
                  return
                }
                
                // For OAuth providers (including Azure), we can't automatically sign in without user interaction
                // But if Azure is the existing provider and user is trying to link password, allow password login
                if (existingProviderId === 'oidc.myazure') {
                  console.log('[SSO][Redirect] Existing provider is Azure - will allow password login to link password to Azure account')
                  
                  // Store flag to link password after Azure login (reverse linking)
                  // When user logs in with Azure, we'll check if password should be linked
                  sessionStorage.setItem('link_password_after_azure', 'true')
                  sessionStorage.setItem('link_password_email', emailToUse)
                  
                  toast(
                    `Your account uses Azure SSO. Please sign in with Azure SSO, then password will be linked automatically.`,
                    { duration: 8000 }
                  )
                  // Don't clear SSO markers - we need them for linking
                  window.location.href = '/login'
                  return
                }
                
                // For other OAuth providers, show helpful message and redirect to login
                console.log('[SSO][Redirect] Existing provider is OAuth - cannot auto-link without user sign-in')
                toast.error(
                  `Your account exists with ${existingProviderName}. Please sign in with ${existingProviderName} first, then Azure SSO will be linked automatically on your next Azure login.`,
                  { duration: 8000 }
                )
                
                // Clear SSO markers
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                sessionStorage.removeItem('sso_timestamp')
                
                // Redirect to login page
                window.location.href = '/login'
                return
                
              } catch (linkError) {
                console.error('[SSO][Redirect] Error during account linking:', linkError)
                console.error('[SSO][Redirect] Link error details:', {
                  code: linkError.code,
                  message: linkError.message,
                  stack: linkError.stack
                })
                
                // Show specific error message based on error type
                let errorMessage = 'Unable to link Azure account. '
                
                if (linkError.code === 'auth/credential-already-in-use') {
                  errorMessage += 'Azure is already linked to another account.'
                } else if (linkError.code === 'auth/email-already-in-use') {
                  errorMessage += 'This email is already in use.'
                } else if (linkError.message?.includes('expired')) {
                  errorMessage += 'The Azure credential has expired. Please try again.'
                } else {
                  errorMessage += 'Please sign in with your existing method first, then Azure will be linked automatically.'
                }
                
                toast.error(errorMessage, { duration: 8000 })
                
                // Clear SSO markers
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                sessionStorage.removeItem('sso_timestamp')
                
                // Redirect to login page
                window.location.href = '/login'
                return
              }
            }
          } catch (linkError) {
            console.error('[SSO][Redirect] Error linking account:', linkError)
            toast.error('Unable to link Azure provider. Please try signing in with your existing method first.')
          }
        } else if (error.code !== 'auth/no-auth-event') {
          // Only show error if it's not a "no redirect result" case
          console.error('[SSO][Redirect] Unexpected error:', error)
          toast.error('SSO redirect error: ' + (error.message || 'Unknown error'))
          // Don't redirect to login on unexpected errors - let the app continue normally
        }
        // If error.code === 'auth/no-auth-event', this is normal (no redirect pending)
        // Don't do anything, just let the app continue
      }
    }

    const checkAuth = async () => {
      // Check if popup login just completed - if so, skip redirect processing
      const popupCompleted = localStorage.getItem('sso_popup_completed')
      const shouldSkipRedirect = popupCompleted && (Date.now() - parseInt(popupCompleted, 10) < 10000)
      
      if (shouldSkipRedirect) {
        console.log('[Auth] Popup login recently completed, skipping redirect processing')
        // Clear the flag after a short delay
        setTimeout(() => {
          localStorage.removeItem('sso_popup_completed')
        }, 2000)
      } else {
        // Clean up old flag if exists
        if (popupCompleted) {
          localStorage.removeItem('sso_popup_completed')
        }
        
        // First, handle any SSO redirect results
        // Add small delay to ensure Firebase has processed redirect
        const storedSSOEmail = sessionStorage.getItem('sso_email')
        if (storedSSOEmail) {
          // We have SSO markers - wait a bit for Firebase to process redirect
          await new Promise(resolve => setTimeout(resolve, 100))
        }
        
        await handleRedirectResult()
      }
      
      const token = localStorage.getItem('token')
      console.log('Auth check - Token found:', !!token)
      if (token) {
        try {
          const response = await authAPI.getCurrentUser()
          console.log('Auth check - API response:', response.data)
          dispatch({
            type: 'SET_USER',
            payload: response.data.data.user
          })
          // Schedule automatic token refresh
          scheduleTokenRefresh()
        } catch (error) {
          console.error('Auth check failed:', error)
          // Try to refresh token if it's expired
          if (error.response?.status === 401) {
            try {
              const refreshResponse = await authAPI.refreshToken()
              if (refreshResponse.data.success && refreshResponse.data.data.token) {
                const newToken = refreshResponse.data.data.token
                localStorage.setItem('token', newToken)
                const userResponse = await authAPI.getCurrentUser()
                dispatch({
                  type: 'SET_USER',
                  payload: userResponse.data.data.user
                })
                scheduleTokenRefresh()
                return
              }
            } catch (refreshError) {
              console.error('Token refresh failed on startup:', refreshError)
            }
          }
          localStorage.removeItem('token')
          dispatch({ type: 'LOGOUT' })
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    // Set up Firebase auth state listener to maintain session
    unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log('Firebase auth state: User is signed in', firebaseUser.uid)
        
        // Check if we just came from a redirect and don't have backend token
        const storedSSOEmail = sessionStorage.getItem('sso_email')
        const isFromRedirect = document.referrer.includes('firebaseapp.com') || 
                               document.referrer.includes('__/auth/handler') ||
                               document.referrer.includes('microsoftonline.com')
        const backendToken = localStorage.getItem('token')
        
        // If Firebase user is signed in but no backend token, and we have SSO markers,
        // complete the login (redirect result might have been missed)
        if (isFromRedirect && storedSSOEmail && !backendToken) {
          console.log('[AuthState] Firebase user signed in after redirect but no backend token - completing login')
          try {
            const storedTenantId = sessionStorage.getItem('sso_tenant_id')
            if (storedTenantId && !auth.tenantId) {
              auth.tenantId = storedTenantId
            }
            
            const idToken = await firebaseUser.getIdToken()
            const normalizeEmail = (email) => email ? email.trim().toLowerCase() : ''
            const emailToUse = normalizeEmail(storedSSOEmail || firebaseUser.email)
            
            if (emailToUse) {
              const response = await authAPI.login({ email: emailToUse, idToken })
              
              if (response.data.requires_mfa && response.data.data?.session_token) {
                sessionStorage.setItem('mfa_session_token', response.data.data.session_token)
                sessionStorage.setItem('mfa_email', emailToUse)
                window.location.href = '/login?mfa=required'
                return
              }
              
              if (response.data.success && response.data.data) {
                const { user, token } = response.data.data
                localStorage.setItem('token', token)
                dispatch({
                  type: 'LOGIN_SUCCESS',
                  payload: { user, token }
                })
                scheduleTokenRefresh()
                
                sessionStorage.removeItem('sso_email')
                sessionStorage.removeItem('sso_tenant_id')
                sessionStorage.removeItem('sso_config')
                
                console.log('[AuthState] Login completed successfully')
                toast.success('SSO login successful!')
                window.location.href = '/dashboard'
                return
              }
            }
          } catch (loginError) {
            console.error('[AuthState] Failed to complete login:', loginError)
            // Don't sign out - let user try again
          }
        }
        
        // Firebase user is signed in, ensure our backend token is still valid
        const token = localStorage.getItem('token')
        if (token) {
          try {
            // Verify token is still valid
            await authAPI.getCurrentUser()
          } catch (error) {
            // Token might be expired, try to refresh
            if (error.response?.status === 401) {
              try {
                const refreshResponse = await authAPI.refreshToken()
                if (refreshResponse.data.success && refreshResponse.data.data.token) {
                  localStorage.setItem('token', refreshResponse.data.data.token)
                  scheduleTokenRefresh()
                }
              } catch (refreshError) {
                console.error('Failed to refresh token on Firebase auth state change:', refreshError)
              }
            }
          }
        }
      } else {
        console.log('Firebase auth state: User is signed out')
        // Only clear token if user explicitly signed out (not on initial load)
        // This prevents clearing token on page refresh
      }
    })

    checkAuth()

    // Cleanup
    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [])

  const login = async (credentials) => {
    dispatch({ type: 'LOGIN_START' })
    try {
      const response = await authAPI.login(credentials)
      
      // Check if MFA is required
      if (response.data.requires_mfa) {
        // Return MFA requirement to caller
        return {
          ...response.data,
          requiresMFA: true
        }
      }
      
      const { user, token } = response.data.data
      
      localStorage.setItem('token', token)
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token }
      })
      
      // Schedule automatic token refresh
      scheduleTokenRefresh()
      
      toast.success('Login successful!')
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed'
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: message
      })
      toast.error(message)
      throw error
    }
  }

  const register = async (userData) => {
    dispatch({ type: 'LOGIN_START' })
    try {
      const response = await authAPI.register(userData)
      const { user, token } = response.data.data
      
      localStorage.setItem('token', token)
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token }
      })
      
      // Schedule automatic token refresh
      scheduleTokenRefresh()
      
      toast.success('Registration successful!')
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed'
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: message
      })
      toast.error(message)
      throw error
    }
  }

  const logout = async () => {
    try {
      // Clear refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      
      // Sign out from Firebase first
      try {
        await auth.signOut()
        console.log('Firebase sign out successful')
      } catch (firebaseError) {
        console.error('Firebase sign out error:', firebaseError)
        // Continue with logout even if Firebase sign out fails
      }
      
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear all auth-related storage
      localStorage.removeItem('token')
      sessionStorage.removeItem('sso_email')
      sessionStorage.removeItem('sso_tenant_id')
      sessionStorage.removeItem('sso_config')
      sessionStorage.removeItem('mfa_session_token')
      sessionStorage.removeItem('mfa_email')
      
      dispatch({ type: 'LOGOUT' })
      toast.success('Logged out successfully')
      
      // Set logout flag in sessionStorage to trigger reset on login page
      sessionStorage.setItem('logout_flag', Date.now().toString())
      
      // Navigate to login page
      // Using window.location.href ensures complete page reload and state reset
      window.location.href = '/login'
    }
  }

  const updateUser = (userData) => {
    dispatch({
      type: 'SET_USER',
      payload: userData
    })
  }

  const hasPermission = (permission) => {
    if (!state.user) return false
    
    // Organization users now have all permissions like control_center users
    if (state.user.user_type === 'organization' || state.user.user_type === 'control_center' || state.user.user_type === 'hub_user') {
      return true
    }
    
    // For other user types, require role
    if (!state.user.role) return false
    
    // All permissions must come from database (role_permissions)
    // Check if role_permissions exists and is a valid object
    if (!state.user.role_permissions || typeof state.user.role_permissions !== 'object') {
      // If role_permissions is missing or invalid, log warning but don't break functionality
      // This handles edge cases where permissions might not be loaded yet
      console.warn(`User ${state.user.id} (role: ${state.user.role}) has no valid role_permissions from database. User will have no permissions until role_permissions is loaded.`)
      return false
    }
    
    const rolePermissions = state.user.role_permissions
    
    // If permissions object is empty, user has no permissions
    if (Object.keys(rolePermissions).length === 0) {
      return false
    }
    
    // If permissions object has '*' key with true value, user has all permissions
    if (rolePermissions['*'] === true) return true
    
    // Check if the specific permission exists and is true
    if (rolePermissions[permission] === true) return true
    
    // Handle singular/plural variations (e.g., organization.read vs organizations.read)
    // This matches the backend logic in Role.hasPermission()
    const permissionParts = permission.split('.')
    if (permissionParts.length === 2) {
      const [resource, action] = permissionParts
      
      // Check singular form (organization.read)
      const singularForm = `${resource.replace(/s$/, '')}.${action}`
      if (rolePermissions[singularForm] === true) return true
      
      // Check plural form (organizations.read)
      const pluralForm = `${resource}${resource.endsWith('s') ? '' : 's'}.${action}`
      if (rolePermissions[pluralForm] === true) return true
    }
    
    return false
  }

  const isControlCenter = () => {
    // Hub users and control_center users both have control center access
    return state.user?.user_type === 'control_center' || state.user?.user_type === 'hub_user'
  }

  const isOrganization = () => {
    return state.user?.user_type === 'organization'
  }

  // Handle MFA login completion (after MFA verification)
  const completeMFALogin = async (userData, token) => {
    localStorage.setItem('token', token)
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user: userData, token }
    })
    // Schedule automatic token refresh
    scheduleTokenRefresh()
  }

  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    hasPermission,
    isControlCenter,
    isOrganization,
    completeMFALogin
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

